import { programWeightLoss } from 'assets/img'
import SEO from 'components/SEO'
import { useEffect, useState, type ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom'
import { cn } from '@cannect/lib/utils'
import {
  COMPARE_ALONE,
  COMPARE_PROGRAM,
  FAQ_ITEMS,
  JOURNEY_STEPS,
  MARQUEE_ITEMS,
  HEADER_NAV,
  PAGE_PATH,
  formatBrl,
  PRICING_ROWS,
  PRICING_SEPARATE_TOTAL,
  PROGRAM_INSTALLMENT_VALUE,
  PROGRAM_MONTHS,
  PROGRAM_PRICE,
  ROTATOR_WORDS,
  SEO_DESCRIPTION,
  SEO_KEYWORDS,
  SEO_TITLE,
  TEAM_TABS,
  WEIGHT_LOSS_PERCENT
} from './constants'
import {
  IconApple,
  IconArrow,
  IconCalendar,
  IconCheck,
  IconClose,
  IconHeart,
  IconInfo,
  IconPulse,
  IconStethoscope,
  ROW_ICONS,
  TAB_ICONS
} from './icons'
import './styles.css'
import { WeightLossProgramTrail } from './trail'
import { buildTrailPath, closeTrailPath, isTrailOpenSearch } from './trail/paths'

const CTA_LABEL = 'Descubra se o programa pode fazer sentido para você'

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `https://cannect.life${PAGE_PATH}`,
      url: `https://cannect.life${PAGE_PATH}`,
      name: SEO_TITLE,
      description: SEO_DESCRIPTION,
      inLanguage: 'pt-BR',
      isPartOf: { '@type': 'WebSite', name: 'Cannect', url: 'https://cannect.life' }
    },
    {
      '@type': 'Offer',
      name: 'Programa de Cuidado · Perda de Peso',
      description: `Programa de ${PROGRAM_MONTHS} meses com acompanhamento multidisciplinar.`,
      price: String(PROGRAM_PRICE),
      priceCurrency: 'BRL',
      availability: 'https://schema.org/InStock',
      url: `https://cannect.life${PAGE_PATH}`
    },
    {
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    }
  ]
}

const useCountUp = (target: number) => {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target)
      return undefined
    }

    let raf = 0
    const startedAt = performance.now()
    const duration = 1400
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration)
      const ease = 1 - (1 - progress) ** 3
      setValue(Math.round(target * ease))
      if (progress < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target])

  return value
}

const CheckBullet = ({ children }: { children: ReactNode }) => (
  <li className="flex items-start gap-2.5 text-base font-medium text-primary-1000">
    <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-marketplace-900 text-white">
      <IconCheck stroke="#FFFFFF" />
    </span>
    {children}
  </li>
)

const CtaLink = ({
  className,
  variant = 'dark'
}: {
  className?: string
  variant?: 'dark' | 'light' | 'sticky' | 'header'
}) => {
  const { search } = useLocation()

  return (
    <Link
      to={buildTrailPath('escolha', undefined, search)}
      className={cn(
        'cta-btn inline-flex min-h-14 items-center justify-center gap-3 rounded-full px-[30px] py-[17px] text-center text-base font-medium no-underline',
        variant === 'dark' && 'wlp-btn-primary bg-marketplace-900 text-white shadow-[0_12px_28px_rgba(59,73,31,.28)]',
        variant === 'light' && 'wlp-btn-light bg-marketplace-300 font-semibold text-primary-1000',
        variant === 'sticky' &&
          'wlp-btn-primary min-h-12 flex-1 bg-marketplace-900 px-[18px] py-3 text-sm leading-[1.3] text-white',
        variant === 'header' &&
          'wlp-btn-primary hide-mobile min-h-11 bg-marketplace-900 px-5 py-2.5 text-sm text-white shadow-none',
        className
      )}>
      {CTA_LABEL}
      {variant !== 'sticky' && variant !== 'header' && <IconArrow className="nudge shrink-0" />}
    </Link>
  )
}

// O link "perguntas frequentes" abre a página já com #duvidas, mas o scroll nativo do hash roda antes de a página
// terminar de montar e de as imagens/fontes mudarem a altura, e acaba no topo. Reposiciona algumas vezes enquanto o
// layout assenta e desiste assim que a pessoa mexe na página.
const useScrollToHash = (hash: string) => {
  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''))
    if (!id) return undefined

    const timers: number[] = []
    const scroll = () => document.getElementById(id)?.scrollIntoView({ block: 'start' })
    const stop = () => timers.forEach((timer) => window.clearTimeout(timer))
    ;[0, 150, 400, 800, 1500, 2500].forEach((delay) => timers.push(window.setTimeout(scroll, delay)))
    window.addEventListener('wheel', stop, { passive: true, once: true })
    window.addEventListener('touchmove', stop, { passive: true, once: true })
    window.addEventListener('keydown', stop, { once: true })

    return () => {
      stop()
      window.removeEventListener('wheel', stop)
      window.removeEventListener('touchmove', stop)
      window.removeEventListener('keydown', stop)
    }
  }, [hash])
}

const WeightLossProgram = () => {
  const history = useHistory()
  const { search, hash } = useLocation()
  useScrollToHash(hash)
  const trailOpen = isTrailOpenSearch(search)
  const [tab, setTab] = useState(0)
  const onlinePct = useCountUp(100)
  const weightPct = useCountUp(WEIGHT_LOSS_PERCENT)
  const price = useCountUp(PROGRAM_INSTALLMENT_VALUE)
  const activeTab = TEAM_TABS[tab]
  const marquee = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS]
  const closeTrail = () => history.replace(closeTrailPath(search))

  return (
    <div className="wlp">
      <SEO title={SEO_TITLE} description={SEO_DESCRIPTION} canonical={PAGE_PATH} keywords={SEO_KEYWORDS} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div
        aria-label="Destaques do programa"
        className="overflow-hidden bg-primary-1000 py-[11px] text-[13px] font-medium tracking-[0.3px] text-primary-100">
        <div className="mq-track">
          <div className="flex whitespace-nowrap pr-10" style={{ gap: 40 }}>
            {marquee.map((item, index) => (
              <span key={`a-${item}-${index}`} aria-hidden={index >= MARQUEE_ITEMS.length}>
                ✦ {item}
              </span>
            ))}
          </div>
          <div className="flex whitespace-nowrap pr-10" style={{ gap: 40 }} aria-hidden>
            {marquee.map((item, index) => (
              <span key={`b-${item}-${index}`}>✦ {item}</span>
            ))}
          </div>
        </div>
      </div>

      <header className="border-b border-solid border-secondary-100 bg-white">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-5 py-4">
          <Link
            to="/"
            className="text-[26px] tracking-[3px] text-primary-800 no-underline"
            aria-label="Cannect — página inicial">
            CANNECT
          </Link>
          <nav className="hide-mobile flex items-center gap-6" aria-label="Seções da página">
            {HEADER_NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm font-medium text-primary-1000 no-underline hover:text-marketplace-800">
                {item.label}
              </a>
            ))}
          </nav>
          <CtaLink variant="header" />
        </div>
      </header>

      <section className="px-5 pt-4">
        <div className="relative mx-auto max-w-[1320px] overflow-hidden rounded-[32px] bg-[#EFE9DF]">
          <div className="hero-row relative mx-auto max-w-[1200px] px-16 py-16">
            <div className="flex min-w-0 flex-1 flex-col gap-5">
              <span className="rise inline-flex items-center gap-2.5 self-start rounded-full bg-white py-[7px] pl-2.5 pr-4 text-[13px] font-medium text-marketplace-900">
                <span className="pulse inline-block size-2.5 rounded-full bg-primary-800" />
                Programa de Cuidado · Perda de Peso
              </span>
              <h1 className="t-h1 rise d1 m-0 text-[54px] font-normal leading-[1.1] tracking-[-0.8px] text-primary-1000">
                Perder peso é só o começo. O objetivo é{' '}
                <span className="relative inline-block whitespace-nowrap">
                  <span className="serif text-marketplace-800">cuidar da sua saúde</span>
                  <svg
                    className="scribble pointer-events-none absolute bottom-[-10px] left-0 h-4 w-full"
                    aria-hidden
                    viewBox="0 0 300 20"
                    preserveAspectRatio="none">
                    <path
                      d="M4 14 C 60 4, 120 4, 180 10 S 260 16, 296 6"
                      fill="none"
                      stroke="#AEBE7B"
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>{' '}
                ao longo de toda a jornada.
              </h1>
              <p className="rise d2 m-0 text-[20px] font-medium text-primary-1000">
                Cuidado com{' '}
                <span className="rotator text-marketplace-800">
                  <span className="rt">
                    {ROTATOR_WORDS.map((word) => (
                      <span key={word}>{word}</span>
                    ))}
                    <span aria-hidden>{ROTATOR_WORDS[0]}</span>
                  </span>
                </span>
              </p>
              <div className="price-box rise d2 flex max-w-[560px] flex-col gap-1.5 rounded-[22px] bg-white px-6 py-5 shadow-[0_10px_30px_rgba(42,47,29,.07)]">
                <span className="text-[15px] text-[#433F3E]">Programa de perda de peso a partir de</span>
                <span className="flex items-baseline gap-1.5 text-primary-1000">
                  <span className="text-[22px] font-medium">R$</span>
                  <span className="price-n text-[60px] font-semibold leading-none tracking-[-1.5px] text-marketplace-900">
                    {price}
                  </span>
                  <span className="text-lg text-[#5A5555]">/mês</span>
                </span>
                <span className="mt-1.5 inline-flex items-center gap-2 self-start rounded-full bg-marketplace-200 px-3.5 py-1.5 text-sm font-medium text-primary-1000">
                  <IconCalendar stroke="#3B491F" />
                  Programa de {PROGRAM_MONTHS} meses
                </span>
              </div>
              <div className="rise d3 mt-1 flex flex-col items-start gap-3">
                <CtaLink />
              </div>
            </div>

            <div className="hero-photo rise d2 relative w-[420px] shrink-0 self-center">
              <svg
                className="spin-slow hero-ring pointer-events-none absolute"
                aria-hidden
                viewBox="0 0 200 200"
                style={{ inset: -44, width: 'calc(100% + 88px)', height: 'calc(100% + 88px)' }}>
                <circle cx="100" cy="100" r="97" fill="none" stroke="#AEBE7B" strokeWidth="1" strokeDasharray="3 6" />
              </svg>
              <div
                className="blob pointer-events-none absolute bg-marketplace-300"
                style={{ inset: -18 }}
                aria-hidden
              />
              <div
                className="blob relative aspect-square overflow-hidden bg-primary-800"
                style={{ animationDuration: '14s', animationDirection: 'reverse' }}>
                <img
                  src={programWeightLoss}
                  alt="Pessoa sorrindo ao iniciar o cuidado com o programa de perda de peso da Cannect"
                  className="h-full w-full scale-110 object-cover object-left"
                  width={420}
                  height={420}
                />
              </div>
              <div className="hero-pill absolute bottom-[-22px] left-1/2 flex -translate-x-1/2 items-center gap-2.5 whitespace-nowrap rounded-full bg-white py-2 pl-2 pr-2.5 shadow-[0_12px_30px_rgba(42,47,29,.14)]">
                <span className="flex">
                  {[
                    { bg: '#E2E7C8', Icon: IconStethoscope },
                    { bg: '#F9F4E7', Icon: IconApple },
                    { bg: '#EDECE7', Icon: IconHeart },
                    { bg: '#D7E0BD', Icon: IconPulse }
                  ].map(({ bg, Icon }, index) => (
                    <span
                      key={bg}
                      className="inline-flex size-[34px] items-center justify-center rounded-full border-2 border-solid border-white"
                      style={{ background: bg, marginLeft: index === 0 ? 0 : -10 }}>
                      <Icon width={16} height={16} stroke="#3B491F" />
                    </span>
                  ))}
                </span>
                <span className="pr-1.5 text-[13px] font-semibold text-primary-1000">Uma equipe cuidando de você</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-5 pb-2 pt-10">
        <div className="stats mx-auto max-w-[1200px]">
          <div className="stat relative flex flex-col items-center justify-center overflow-hidden rounded-[20px] bg-marketplace-100 px-6 py-[26px] text-center">
            <span className="blob st-deco pointer-events-none absolute -right-10 -top-10 size-[120px] bg-primary-100" />
            <span className="st-ico float relative mb-1.5 inline-flex size-14 items-center justify-center rounded-2xl bg-white shadow-[0_8px_18px_rgba(42,47,29,.10)]">
              <svg
                width="34"
                height="34"
                viewBox="0 0 34 34"
                fill="none"
                stroke="#3B491F"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden>
                <rect x="11" y="12" width="12" height="19" rx="2.5" />
                <path d="M16 27.5h2" />
                <path className="wv" d="M14 8.5a5 5 0 0 1 6 0" />
                <path className="wv w2" d="M11.5 5.5a9 9 0 0 1 11 0" />
                <path className="wv w3" d="M9 2.5a13 13 0 0 1 16 0" />
              </svg>
            </span>
            <span className="stat-n relative text-[48px] font-semibold leading-none text-marketplace-900">
              {onlinePct}%
            </span>
            <span className="stat-l relative text-sm leading-[1.45] text-[#433F3E]">
              online, do diagnóstico ao acompanhamento
            </span>
          </div>
          <div className="stat relative flex flex-col items-center justify-center overflow-hidden rounded-[20px] bg-marketplace-900 px-6 py-[26px] text-center">
            <span className="blob st-deco pointer-events-none absolute -bottom-[50px] -left-[50px] size-[140px] bg-marketplace-800" />
            <span
              className="st-ico float relative mb-1.5 inline-flex size-14 items-center justify-center rounded-2xl bg-marketplace-800"
              style={{ animationDelay: '.6s' }}>
              <svg
                width="38"
                height="30"
                viewBox="0 0 38 30"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden>
                <path d="M3 27h32" stroke="#6B7B3C" strokeWidth="1.5" />
                <path className="drawloop" d="M4 5 L12 11 L18 9 L26 18 L34 22" stroke="#D7E0BD" strokeWidth="2.6" />
                <circle className="dotpop" cx="34" cy="22" r="3" fill="#FFFFFF" />
              </svg>
            </span>
            <span className="stat-l relative text-sm text-marketplace-300">Elimine até</span>
            <span className="stat-n relative text-[48px] font-semibold leading-none text-white">{weightPct}%</span>
            <span className="stat-l relative text-sm leading-[1.45] text-primary-100">do seu peso corporal</span>
          </div>
          <div className="stat relative flex flex-col items-center justify-center overflow-hidden rounded-[20px] bg-marketplace-100 px-6 py-[26px] text-center">
            <span className="blob st-deco pointer-events-none absolute -bottom-[50px] -right-10 size-[120px] bg-primary-100" />
            <span className="relative mb-3.5 inline-flex size-14 items-center justify-center">
              <span className="ripple absolute inset-0 rounded-full border-2 border-solid border-primary-800" />
              <span className="ripple r2 absolute inset-0 rounded-full border-2 border-solid border-primary-800" />
              <span className="relative inline-flex size-14 items-center justify-center rounded-full bg-marketplace-900">
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden>
                  <circle cx="15" cy="15" r="11" />
                  <path d="M15 8v7" />
                  <g className="hand">
                    <path d="M15 15l5 3" stroke="#D7E0BD" strokeWidth="2.4" />
                  </g>
                </svg>
              </span>
            </span>
            <span className="stat-n relative text-[48px] font-semibold leading-none text-marketplace-900">
              Avaliação médica
            </span>
            <span className="stat-l relative text-sm leading-[1.45] text-[#433F3E]">para começar o seu cuidado</span>
          </div>
        </div>
        <p className="mx-auto mt-2.5 max-w-[1200px] text-center text-[11.5px] text-[#726C6C]">
          Os resultados são individuais e dependem da avaliação e do acompanhamento de cada paciente.
        </p>
      </section>

      <section id="incluido" className="section-anchor px-5 py-8">
        <div className="sec-card mx-auto max-w-[1320px] rounded-[32px] bg-marketplace-200 p-16">
          <div className="mx-auto flex max-w-[1100px] flex-col gap-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="text-[13px] font-semibold uppercase tracking-[1.5px] text-marketplace-800">
                O que está incluído
              </span>
              <h2 className="t-h2 m-0 text-[40px] font-normal leading-[1.2] text-primary-1000">
                Conheça quem vai <span className="serif text-marketplace-800">cuidar de você</span>
              </h2>
              <span className="text-sm text-[#5A5555]">Toque em cada especialidade para ver o que está incluído</span>
            </div>
            <div className="team">
              <div className="tabs" role="tablist" aria-label="Especialidades">
                {TEAM_TABS.map((item, index) => {
                  const Icon = TAB_ICONS[index]
                  const selected = tab === index
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      id={`wlp-tab-${item.id}`}
                      aria-selected={selected}
                      aria-controls={`wlp-panel-${item.id}`}
                      tabIndex={selected ? 0 : -1}
                      className={cn('tab', selected && 'on')}
                      onClick={() => setTab(index)}
                      onKeyDown={(event) => {
                        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                          event.preventDefault()
                          setTab((tab + 1) % TEAM_TABS.length)
                        }
                        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                          event.preventDefault()
                          setTab((tab + TEAM_TABS.length - 1) % TEAM_TABS.length)
                        }
                      }}>
                      <span className="tab-ico">
                        <Icon stroke="#3B491F" />
                      </span>
                      {item.label}
                    </button>
                  )
                })}
              </div>
              <div
                className="panel-box flex min-h-[260px] flex-col justify-center rounded-3xl bg-white p-9"
                role="tabpanel"
                id={`wlp-panel-${activeTab.id}`}
                aria-labelledby={`wlp-tab-${activeTab.id}`}>
                <div className="panel flex flex-col gap-4">
                  <span className="text-xs font-semibold uppercase tracking-[1.2px] text-marketplace-800">
                    {activeTab.label}
                  </span>
                  <p className="m-0 text-[22px] leading-[1.35] text-primary-1000">{activeTab.description}</p>
                  <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                    {activeTab.items.map((item) => (
                      <CheckBullet key={item}>{item}</CheckBullet>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="valores" className="section-anchor px-5 pb-8">
        <div className="sec-card relative mx-auto max-w-[1320px] overflow-hidden rounded-[32px] bg-[#EFE9DF] p-16">
          <div className="blob hide-mobile pointer-events-none absolute -right-[140px] -top-40 size-[380px] bg-primary-100 opacity-70" />
          <div className="relative mx-auto flex max-w-[1000px] flex-col gap-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="text-[13px] font-semibold uppercase tracking-[1.5px] text-marketplace-800">
                Tudo em um só lugar
              </span>
              <h2 className="t-h2 m-0 text-[40px] font-normal leading-[1.2] text-primary-1000">
                Quanto custaria fazer <span className="serif text-marketplace-800">tudo separado?</span>
              </h2>
              <span className="text-[15px] text-[#5A5555]">
                Compare e veja tudo o que o programa reúne em um único valor.
              </span>
            </div>
            <div className="pt" role="table" aria-label="Comparativo de valores">
              <div className="c h" role="columnheader">
                <span className="sr-only">Item</span>
              </div>
              <div className="c h" role="columnheader">
                Separado
              </div>
              <div className="c h hpg" role="columnheader">
                <span className="best">Tudo incluso</span>
                Programa Cannect
              </div>
              {PRICING_ROWS.map((row, index) => {
                const Icon = ROW_ICONS[index]
                return (
                  <div key={row.id} className="contents">
                    <div className="c it" role="rowheader">
                      <span className="row-ico">
                        <Icon stroke="#3B491F" />
                      </span>
                      <span>{row.label}</span>
                    </div>
                    <div className="c sep" role="cell">
                      {formatBrl(row.separatePrice)}
                    </div>
                    <div className="c pg" role="cell">
                      <span
                        className={cn(
                          'ck',
                          `ck${index + 1}`,
                          'inline-flex size-[22px] shrink-0 items-center justify-center rounded-full bg-marketplace-300'
                        )}>
                        <IconCheck size={12} stroke="#2A2F1D" strokeWidth={3.2} />
                      </span>
                      Incluso
                    </div>
                  </div>
                )
              })}
              <div className="c it tot" role="rowheader">
                Total
              </div>
              <div className="c sep tot" role="cell">
                <span className="strike">{formatBrl(PRICING_SEPARATE_TOTAL)}</span>
              </div>
              <div className="c pg tot pg-last" role="cell">
                <span className="flex flex-col items-center leading-tight">
                  <span className="text-xs font-normal text-marketplace-300">a partir de</span>
                  <span className="tot-price">
                    R$ {price}
                    <span className="text-sm font-normal">/mês</span>
                  </span>
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3 text-center">
              <CtaLink />
              <p className="m-0 max-w-[720px] text-xs leading-[1.6] text-[#5A5555]">
                Valores avulsos são uma referência média de mercado e podem variar conforme região e profissional.
                *Tratamento medicamentoso somente quando houver indicação clínica e prescrição médica, conforme
                condições do programa.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="section-anchor sec-pad px-5 py-16">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-10">
          <div className="flex flex-col items-center gap-2 text-center">
            <span className="text-[13px] font-semibold uppercase tracking-[1.5px] text-marketplace-800">
              Como funciona a jornada
            </span>
            <h2 className="t-h2 m-0 text-[40px] font-normal leading-[1.2] text-primary-1000">
              Um passo de cada vez, <span className="serif text-marketplace-800">sempre acompanhado</span>
            </h2>
          </div>
          <div className="relative">
            <span className="tl-line" aria-hidden>
              <span className="tl-progress block h-full w-full bg-primary-800" />
            </span>
            <ol className="steps m-0 list-none p-0">
              {JOURNEY_STEPS.map((step, index) => (
                <li key={step.n} className="step relative flex flex-col gap-3.5">
                  <span
                    className={cn(
                      'relative z-[1] inline-flex size-14 shrink-0 items-center justify-center rounded-full text-xl font-semibold',
                      index === 0
                        ? 'pulse bg-marketplace-900 text-white'
                        : 'box-border border-[3px] border-solid border-primary-800 bg-white text-marketplace-900'
                    )}>
                    {step.n}
                  </span>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    {'badge' in step && step.badge && (
                      <span className="self-start rounded-full bg-marketplace-300 px-2.5 py-[3px] text-[11px] font-semibold uppercase tracking-[0.5px] text-primary-1000">
                        {step.badge}
                      </span>
                    )}
                    <h3 className="m-0 text-lg font-medium text-primary-1000">{step.title}</h3>
                    <p className="m-0 text-sm leading-[1.55] text-[#5A5555]">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div
            role="note"
            aria-label="Informação importante"
            className="flex items-start gap-3 rounded-[14px] border border-solid border-[#E8DFC6] bg-primary-50 px-[18px] py-3.5">
            <IconInfo className="mt-px shrink-0" stroke="#3B491F" />
            <p className="m-0 text-[13.5px] leading-[1.6] text-[#433F3E]">
              <strong className="font-semibold text-primary-1000">Informação importante:</strong> A indicação de
              tratamento medicamentoso, escolha do medicamento, dose e ajustes terapêuticos são realizados
              exclusivamente pelo médico responsável após avaliação clínica individualizada.
            </p>
          </div>
        </div>
      </section>

      <section className="px-5 pb-8">
        <div className="sec-card mx-auto max-w-[1320px] rounded-[32px] bg-primary-50 p-16">
          <div className="mx-auto flex max-w-[1000px] flex-col gap-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <span className="text-[13px] font-semibold uppercase tracking-[1.5px] text-marketplace-800">
                Mais do que um número na balança
              </span>
              <h2 className="t-h2 m-0 text-[40px] font-normal leading-[1.2] text-primary-1000">
                Por que fazer <span className="serif text-marketplace-800">com acompanhamento?</span>
              </h2>
            </div>
            <div className="cmp">
              <div className="cmp-card flex flex-col gap-4 rounded-3xl border border-solid border-[#E8DFC6] bg-white p-7">
                <span className="text-base font-semibold text-[#5A5555]">Tentando sozinho(a)</span>
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                  {COMPARE_ALONE.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-[15px] text-[#5A5555]">
                      <IconClose className="mt-px shrink-0" stroke="#B3B3B3" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="cmp-card flex flex-col gap-4 rounded-3xl bg-marketplace-900 p-7 shadow-[0_18px_40px_rgba(42,47,29,.22)]">
                <span className="text-base font-semibold text-white">Com o Programa de Cuidado</span>
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                  {COMPARE_PROGRAM.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-[15px] text-white">
                      <IconCheck size={20} className="mt-px shrink-0" stroke="#D7E0BD" strokeWidth={2.6} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex justify-center">
              <CtaLink />
            </div>
          </div>
        </div>
      </section>

      <section id="duvidas" className="section-anchor sec-pad px-5 py-14">
        <div className="faq-row mx-auto flex max-w-[1200px] gap-16">
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <span className="text-[13px] font-semibold uppercase tracking-[1.5px] text-marketplace-800">
              Dúvidas frequentes
            </span>
            <h2 className="t-h2 m-0 text-[36px] font-normal leading-[1.2] text-primary-1000">
              Ficou com alguma <span className="serif text-marketplace-800">dúvida?</span>
            </h2>
          </div>
          <div className="flex min-w-0 flex-[2] flex-col gap-2.5">
            {FAQ_ITEMS.map((item) => (
              <details
                key={item.question}
                className="rounded-[14px] border border-solid border-[#E3E3E3] bg-[#FAFAFA] px-5">
                <summary className="flex cursor-pointer items-center justify-between gap-4 py-[18px] text-base font-medium text-primary-1000">
                  {item.question}
                  <span className="faq-plus text-[22px] text-marketplace-800 transition-transform duration-200">+</span>
                </summary>
                <p className="m-0 pb-[18px] text-[15px] leading-[1.6] text-[#5A5555]">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="triagem" className="section-anchor px-5 pb-8">
        <div className="relative mx-auto max-w-[1320px] overflow-hidden rounded-[32px] bg-primary-1000">
          <div className="blob pointer-events-none absolute -right-[120px] -top-[140px] size-[420px] bg-marketplace-900" />
          <div
            className="blob pointer-events-none absolute -bottom-40 -left-[100px] size-[340px] bg-marketplace-800 opacity-50"
            style={{ animationDuration: '16s' }}
          />
          <div className="relative mx-auto flex max-w-[760px] flex-col items-center gap-4 px-6 py-[72px] text-center">
            <h2 className="t-h2 m-0 text-[44px] font-normal leading-[1.2] text-white">
              Comece pelo <span className="serif text-marketplace-300">primeiro passo</span> da sua jornada
            </h2>
            <p className="m-0 text-[17px] leading-[1.6] text-primary-100">
              Responda algumas perguntas e veja se o programa é indicado para você.
            </p>
            <CtaLink variant="light" className="mt-2" />
          </div>
        </div>
      </section>

      <footer className="border-t border-solid border-secondary-100 px-5 py-6">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-3 text-xs leading-[1.6] text-[#5A5555]">
          <Link
            to="/"
            className="text-lg tracking-[2px] text-primary-800 no-underline"
            aria-label="Cannect — página inicial">
            CANNECT
          </Link>
          <span>Este conteúdo é informativo e não substitui a avaliação médica.</span>
        </div>
      </footer>

      {trailOpen ? null : (
        <div className="sticky-cta fixed bottom-0 left-0 right-0 z-40 border-t border-solid border-secondary-100 bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(42,47,29,0.08)]">
          <CtaLink variant="sticky" />
        </div>
      )}

      {trailOpen ? <WeightLossProgramTrail onClose={closeTrail} /> : null}
    </div>
  )
}

export const RedirectToWeightLossProgram = () => {
  const { search } = useLocation()
  return <Redirect to={{ pathname: PAGE_PATH, search }} />
}

export { WeightLossProgram }
export default WeightLossProgram
